import type { EffectiveSetting } from '@zuruflex/api-client';
import {
  Badge,
  Button,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Waypoints } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Branches whose values can differ.
 *
 * Tax is the reason this page is scoped at all — VAT is 15% in Ghana and
 * 18% in Rwanda, so a single global figure is wrong for one of them.
 */
const SCOPES = [
  { label: 'All branches', countryCode: '', currency: '' },
  { label: 'Rwanda (RWF)', countryCode: 'RW', currency: '' },
  { label: 'Ghana (GHS)', countryCode: 'GH', currency: '' },
] as const;

/**
 * Which groups the page renders, in order.
 *
 * A group missing from here is unreachable — `review` was, so every review
 * setting existed in the catalogue and could only be changed by writing to
 * the table by hand.
 */
const GROUP_ORDER = [
  'tax',
  'discount',
  'payment',
  'partner',
  'review',
  'badge',
  'messaging',
  'referral',
] as const;

const GROUP_LABEL: Record<string, string> = {
  tax: 'Tax',
  discount: 'Long-hire discounts',
  payment: 'Payment at booking',
  partner: 'Car owners',
  review: 'Reviews and ratings',
  badge: 'Listing badges',
  messaging: 'Messaging',
  referral: 'Referrals',
};

/** A rate is stored as 0.18 and shown as 18 — nobody types "0.18" for VAT. */
function toDisplay(setting: EffectiveSetting, value: number | boolean | string): string {
  if (setting.type === 'rate' && typeof value === 'number') {
    // Rounded because 0.07 * 100 is 7.000000000000001.
    return String(Math.round(value * 1000) / 10);
  }
  return String(value);
}

function fromDisplay(setting: EffectiveSetting, raw: string): number | string | boolean {
  if (setting.type === 'rate') return Math.round(Number(raw) * 10) / 1000;
  // Money is minor units, as typed: a whole number, never a string.
  if (setting.type === 'integer' || setting.type === 'money') return Number(raw);
  // The catalogue rejects the string "true" for a boolean, on purpose.
  if (setting.type === 'boolean') return raw === 'true';
  return raw;
}

export function SettingsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Provider routing is read at MANAGER and above; an agent would only meet a 403.
  const canSeeProviders = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const [scopeIndex, setScopeIndex] = useState(0);
  const scope = SCOPES[scopeIndex] ?? SCOPES[0];

  /** Edits not yet saved, keyed by setting. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<Record<string, string>>({});

  const scopeQuery = { countryCode: scope.countryCode, currency: scope.currency };

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['settings', scope.countryCode, scope.currency],
    queryFn: () => api.listSettings(scopeQuery),
  });

  const save = useMutation({
    mutationFn: (input: { key: string; value: number | boolean | string }) =>
      api.saveSetting({ ...input, ...scopeQuery }),
    onSuccess: (_result, input) => {
      setDrafts((current) => {
        const next = { ...current };
        delete next[input.key];
        return next;
      });
      setFailed((current) => {
        const next = { ...current };
        delete next[input.key];
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (cause: unknown, input) => {
      // The API explains exactly which bound was broken; showing it beside
      // the field is the difference between a fixable form and a dead end.
      setFailed((current) => ({
        ...current,
        [input.key]: cause instanceof Error ? cause.message : 'Could not save',
      }));
    },
  });

  const clear = useMutation({
    mutationFn: (key: string) => api.clearSetting(key, scopeQuery),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const settings = data?.settings ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('admin:settings.title', 'Settings')}
        description={t(
          'admin:settings.description',
          'Rates and rules the business runs on. Changes apply to the next quote — no deploy needed.',
        )}
        action={
          canSeeProviders ? (
            <Button variant="outline" asChild>
              <Link to="/settings/providers">
                <Waypoints />
                {t('admin:settings.providers', 'Providers')}
              </Link>
            </Button>
          ) : undefined
        }
      />

      <div className="max-w-xs">
        <FormField
          label={t('admin:settings.scope', 'Branch')}
          hint={t(
            'admin:settings.scopeHint',
            'A branch value overrides the all-branches one. Tax differs by country.',
          )}
        >
          {() => (
            <Select
              value={String(scopeIndex)}
              onValueChange={(value) => setScopeIndex(Number(value))}
            >
              <SelectTrigger className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPES.map((option, index) => (
                  <SelectItem key={option.label} value={String(index)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
      </div>

      {isPending ? (
        <p className="text-sm text-text-muted">{t('common:state.loading', 'Loading…')}</p>
      ) : (
        GROUP_ORDER.map((group) => {
          const inGroup = settings.filter((setting) => setting.group === group);
          if (inGroup.length === 0) return null;

          return (
            <section key={group} className="space-y-4">
              <h2 className="text-lg font-semibold text-text">
                {t(`admin:settings.group.${group}`, GROUP_LABEL[group] ?? group)}
              </h2>

              <div className="grid gap-4 rounded-card border border-border-default bg-surface-raised p-6">
                {inGroup.map((setting) => {
                  const draft = drafts[setting.key];
                  const current = toDisplay(setting, setting.value);
                  const shown = draft ?? current;
                  const dirty = draft !== undefined && draft !== current;

                  return (
                    <div
                      key={setting.key}
                      className="grid gap-3 border-b border-border-default pb-4 last:border-0 last:pb-0 sm:grid-cols-[1fr_auto]"
                    >
                      <FormField
                        label={
                          <span className="flex items-center gap-2">
                            {setting.label}
                            {setting.isOverridden ? (
                              <Badge variant="warning">
                                {t('admin:settings.overridden', 'Overridden here')}
                              </Badge>
                            ) : null}
                          </span>
                        }
                        hint={setting.help}
                        error={failed[setting.key]}
                      >
                        {(field) =>
                          setting.type === 'boolean' ? (
                            <Select
                              value={shown === 'true' ? 'true' : 'false'}
                              onValueChange={(value) =>
                                setDrafts((d) => ({ ...d, [setting.key]: value }))
                              }
                            >
                              <SelectTrigger id={field.id} className="h-10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">
                                  {t('admin:settings.on', 'On')}
                                </SelectItem>
                                <SelectItem value="false">
                                  {t('admin:settings.off', 'Off')}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : setting.type === 'enum' ? (
                            <Select
                              value={shown}
                              onValueChange={(value) =>
                                setDrafts((d) => ({ ...d, [setting.key]: value }))
                              }
                            >
                              <SelectTrigger id={field.id} className="h-10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(setting.options ?? []).map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : setting.type === 'text' ? (
                            <Input
                              {...field}
                              value={shown}
                              maxLength={setting.max}
                              onChange={(event) =>
                                setDrafts((d) => ({ ...d, [setting.key]: event.target.value }))
                              }
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                {...field}
                                type="number"
                                inputMode="decimal"
                                value={shown}
                                step={setting.type === 'rate' ? 0.1 : 1}
                                onChange={(event) =>
                                  setDrafts((d) => ({ ...d, [setting.key]: event.target.value }))
                                }
                              />
                              {/* Percent in the box, rate in the database. */}
                              {setting.type === 'rate' ? (
                                <span className="text-sm text-text-muted">%</span>
                              ) : null}
                            </div>
                          )
                        }
                      </FormField>

                      <div className="flex items-end gap-2">
                        <Button
                          disabled={!dirty || save.isPending}
                          onClick={() =>
                            save.mutate({
                              key: setting.key,
                              value: fromDisplay(setting, shown),
                            })
                          }
                        >
                          {t('common:action.save', 'Save')}
                        </Button>
                        {setting.isOverridden ? (
                          <Button
                            variant="ghost"
                            disabled={clear.isPending}
                            onClick={() => clear.mutate(setting.key)}
                          >
                            {t('admin:settings.reset', 'Reset')}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
